/**
 * Helper method to append zeros to the string so that it has the right precision
 * 
 * @param number The number of zeros we are wanting to append to the string
 */
function append(number: number) {
  let zeros = '';
  for (let i = 0; i < number; ++i) zeros += '0';
  return zeros;
}

/**
 * Helper method to get the hunderds place for the precision
 * 
 * @param precision The precision we want to get the rounder for, this will look like 10, 100, 100, .....
 */
function getRounder(precision: number) {
  if (precision < 0) throw new RangeError('[precision] cannot be below 0');
  if (precision > 16) throw new RangeError('[precision] factor cannot be greater than 16');
  let rounder = 1;
  for (let i = 0; i < precision; ++i) {
    rounder *= 10;
  }
  return rounder;
}

/**
 * Helper method to round the current value to the proper value
 * 
 * @param value The value we need to round based on the precision
 * @param precision The precision amount we are wanting to use
 * @param roundFunction The round function we want to use when handling the round
 */
function round(value: number, precision: number, roundFunction: 'round' | 'floor' | 'ceil') {
  if (Math[roundFunction] === undefined) throw new EvalError('[roundFunction] Can only use round, floor or ceil');
  if (precision !== 0) {
    const rounder = getRounder(precision);
    return Math[roundFunction](rounder * value) / rounder;
  }
  return value;
}

/**
 * Helper method to format a value string and add/remove precision from the string
 * 
 * @param value The value that should be formated
 * @param precision The precision we are working with when formatting this number
 */
function format(value: string, precision: number) {
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) value += `.${append(precision)}`;
  else {
    const delta = value.length - dotIndex - 1 - precision;
    if (delta < 0) {
      value += append(delta * -1);
    } else {
      value = value.substr(0, value.length - delta);
    }
  }
  return value;
}

/**
 * Custom Filter to format a number/string to a string precision for viewing on the screen
 * 
 * @param value The value we need to process with the precision given
 * @param precision The current precision we want to format to
 * @param roundFunction The round function we will currently be using with the precision
 */
export function precision(value: string | number, precision = 0, roundFunction: 'round' | 'floor' | 'ceil' = 'round') {
  const innerValue = round(typeof value === 'string' ? parseFloat(value) : value, precision, roundFunction);
  return format(innerValue.toString(), precision);
}